/* Demo Backlog page — shows items with status "backlog".
   Users can queue or complete items. Groups by section when on "all" tab. */

import { useState, useMemo } from "react";
import { ContentTypeTabs } from "@/components/feed/ContentTypeTabs";
import { Tabs } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Check, PlusCircle } from "lucide-react";
import type { DemoItem } from "./demoData";
import { getSectionForTag } from "./demoData";
import { DemoFeedItemRow } from "./DemoFeedItemRow";


const HIGHLIGHT_CLASS = "ring-2 ring-primary/60 rounded-md transition-all duration-300";

interface DemoBacklogPageProps {
  items: DemoItem[];
  onQueueItem: (id: string) => void;
  onCompleteItem: (id: string) => void;
  highlightedFeature?: string;
}

export function DemoBacklogPage({
  items,
  onQueueItem,
  onCompleteItem,
  highlightedFeature,
}: DemoBacklogPageProps) {
  const [activeTab, setActiveTab] = useState("all");
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const backlogItems = items.filter((item) => item.status === "backlog");

  const availableTags = useMemo(
    () => [...new Set(backlogItems.flatMap((item) => item.tags))].sort(),
    [backlogItems],
  );

  const filtered = useMemo(() => {
    let result = backlogItems;
    if (activeTab !== "all") {
      result = result.filter((item) => item.contentType === activeTab);
    }
    if (filterTag) {
      result = result.filter((item) => item.tags.includes(filterTag));
    }
    return [...result].sort((a, b) =>
      sortOrder === "asc" ? a.addedAt - b.addedAt : b.addedAt - a.addedAt,
    );
  }, [backlogItems, activeTab, filterTag, sortOrder]);

  const showSections = activeTab === "all" && !filterTag;

  const groupedItems = useMemo(() => {
    if (!showSections) return filtered;
    return [...filtered].sort((a, b) => {
      const aSection = availableTags.indexOf(a.tags[0]);
      const bSection = availableTags.indexOf(b.tags[0]);
      if (aSection !== bSection) return aSection - bSection;
      return sortOrder === "asc" ? a.addedAt - b.addedAt : b.addedAt - a.addedAt;
    });
  }, [filtered, showSections, sortOrder, availableTags]);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
      <div className="sticky top-0 z-20 bg-background">
        <div className="flex items-center gap-3 px-4 md:px-6 pt-4 pb-2">
          <h1 className="text-lg font-bold text-heading-foreground">Backlog</h1>
          <Badge variant="secondary" className="text-xs">
            {backlogItems.length}
          </Badge>
        </div>
        <div className={highlightedFeature === "content-filter" || highlightedFeature === "tag-filter" || highlightedFeature === "sort-order" ? HIGHLIGHT_CLASS : ""}>
          <ContentTypeTabs
            activeTab={activeTab}
            onActiveTabChange={setActiveTab}
            showForYou
            provocativeCount={1}
            filterTag={filterTag}
            onFilterTagChange={setFilterTag}
            availableTags={availableTags}
            sortOrder={sortOrder}
            onSortOrderChange={setSortOrder}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {groupedItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <p className="text-sm">No items in this category</p>
          </div>
        ) : (
          groupedItems.map((item, i) => {
            const sectionName = showSections ? getSectionForTag(item.tags[0]) : null;
            const prevSection = i > 0 && showSections ? getSectionForTag(groupedItems[i - 1].tags[0]) : null;
            const showHeader = sectionName !== null && sectionName !== prevSection;

            const nextSection = i < groupedItems.length - 1 && showSections ? getSectionForTag(groupedItems[i + 1].tags[0]) : null;
            const isLastInSection = i === groupedItems.length - 1 || nextSection !== sectionName;

            return (
              <div key={item.id}>
                {showHeader && (
                  <SectionHeader name={sectionName} highlighted={highlightedFeature === "sections" && prevSection === null} />
                )}
                <DemoFeedItemRow
                  item={item}
                  actions={[
                    {
                      icon: Check,
                      onClick: () => onCompleteItem(item.id),
                      label: "Complete",
                    },
                    {
                      icon: PlusCircle,
                      onClick: () => onQueueItem(item.id),
                      label: "Add to queue",
                    },
                  ]}
                  hideBorder={isLastInSection}
                />
              </div>
            );
          })
        )}
      </div>
    </Tabs>
  );
}

function SectionHeader({ name, highlighted }: { name: string; highlighted?: boolean }) {
  return (
    <div className={`sticky z-10 bg-background flex items-center gap-3 px-4 md:px-6 pt-5 pb-2 ${highlighted ? HIGHLIGHT_CLASS : ""}`}>
      <span className="text-xs font-semibold uppercase tracking-wider text-primary whitespace-nowrap">
        {name}
      </span>
      <div className="flex-1 border-t border-border" />
    </div>
  );
}
